import { useEffect, useState } from 'react'
import { api, errorText } from '../api/client'
import type { Automation, Option } from '../api/types'
import { titleCase } from '../lib/format'
import { useAsync } from '../lib/useAsync'
import { Icon } from './Icon'
import { useToast } from './Toast'

/** Event labels are written as sentences ("A new lead is added"); they read as a
 *  clause after "When". */
const lower1 = (s: string) => s.charAt(0).toLowerCase() + s.slice(1)

/** Identity of a rule as the server sees it — the upsert key is event + playbook +
 *  owner + filter, so this is what tells a suggestion from an existing rule. */
const ruleKey = (event: string, playbook: string, filter: Record<string, string> | null) =>
  `${event}|${playbook}|${filter ? JSON.stringify(filter) : ''}`

/** "When X happens, start workflow Y." Lives on the Workflows page because it
 *  answers a workflow question — how does one start without me clicking Start?
 *  Rules save the moment they change; there is no separate Save. */
export function AutomationRules({ isAdmin }: { isAdmin: boolean }) {
  const { data } = useAsync(() => api.customize(), [])
  // Lead statuses aren't in the customize payload, and a rule's "moves to" filter
  // must offer exactly the values the server matches on.
  const { data: vocab } = useAsync(() => api.vocabulary(), [])
  const [rules, setRules] = useState<Automation[]>([])
  const [draft, setDraft] = useState({ event: '', playbook: '', to: '', scope: 'mine' as 'mine' | 'branch' })
  const [adding, setAdding] = useState(false)
  const [busy, setBusy] = useState(false)
  const toast = useToast()

  useEffect(() => { if (data) setRules(data.automations) }, [data])

  if (!data) return null

  async function write(rule: { event: string; playbook: string; scope: 'mine' | 'branch'; filter: Record<string, string> | null; active: boolean }, done?: string) {
    setBusy(true)
    try {
      setRules((await api.setAutomation(rule)).automations)
      if (done) toast(done, { tone: 'positive' })
      return true
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
      return false
    } finally {
      setBusy(false)
    }
  }

  async function remove(a: Automation) {
    setBusy(true)
    try {
      setRules((await api.deleteAutomation(a.id)).automations)
      toast(`${a.playbook_name} will no longer start by itself.`, { tone: 'positive' })
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    } finally {
      setBusy(false)
    }
  }

  const event = draft.event || data.events[0]?.key || ''
  const playbook = draft.playbook || data.playbooks[0]?.key || ''
  const filterOptions: Option[] =
    event === 'lead_status_changed' ? vocab?.lead_status ?? []
      : event === 'loan_stage_changed' ? data.vocabulary.map((s) => ({ key: s.key, label: s.label }))
        : []
  const stageLabel = (key: string) =>
    data.vocabulary.find((s) => s.key === key)?.label
    ?? (vocab?.lead_status ?? []).find((s) => s.key === key)?.label ?? titleCase(key)
  /** "a lead moves to a stage" + {to: application} → "a lead moves to Application". */
  const when = (label: string, f: Record<string, string> | null) => {
    const clause = lower1(label)
    return f?.to ? `${clause.replace(/ to a stage$/, '')} to ${stageLabel(f.to)}` : clause
  }
  const eventLabel = (key: string) => data.events.find((e) => e.key === key)?.label ?? titleCase(key)
  const playbookName = (key: string) => data.playbooks.find((p) => p.key === key)?.name ?? titleCase(key)
  const categories = [...new Set(data.playbooks.map((p) => p.category))]
  const configured = new Set(rules.map((a) => ruleKey(a.event, a.playbook, a.filter)))
  const suggestions = data.suggested_automations.filter((s) => !configured.has(ruleKey(s.event, s.playbook, s.filter)))

  async function add() {
    const filter = draft.to ? { to: draft.to } : null
    if (configured.has(ruleKey(event, playbook, filter))) return toast('That rule already exists.', { tone: 'warning' })
    if (await write({ event, playbook, scope: draft.scope, filter, active: true }, 'Done — it starts the next time that happens.')) {
      setAdding(false)
      setDraft((d) => ({ ...d, to: '' }))
    }
  }

  return (
    <section className="card">
      <div className="card-head">
        <Icon name="bolt" size={18} style={{ color: 'var(--accent)' }} />
        <h2 className="h2">Start automatically</h2>
        <span className="spacer" />
        {!adding ? (
          <button className="btn sm" onClick={() => setAdding(true)}><Icon name="add" size={15} /> Add a rule</button>
        ) : null}
      </div>
      <div className="card-body" style={{ paddingTop: 0 }}>
        <p className="quiet" style={{ marginTop: 0, fontSize: 12.5 }}>
          Instead of pressing Start each time, have a workflow begin on its own when something happens.
          Nothing starts automatically unless you add a rule here.
        </p>

        {rules.length === 0 && !adding ? (
          <p style={{ fontSize: 13, margin: '6px 0' }}>No rules yet — every workflow starts only when someone presses Start.</p>
        ) : null}

        {rules.map((a) => (
          <div className="stagerow" key={a.id} style={{ opacity: a.active ? 1 : 0.55 }}>
            <span style={{ flex: 1, minWidth: 0, fontSize: 13 }}>
              When <strong>{when(a.event_label, a.filter)}</strong>, start <strong>{a.playbook_name}</strong>
              <span style={{ display: 'block', fontSize: 11.5, color: 'var(--ink-3)' }}>
                {a.scope === 'branch' ? 'For everyone at the branch' : 'For your contacts'}{a.active ? '' : ' · switched off'}
              </span>
            </span>
            <button className={`toggle ${a.active ? 'on' : 'off'}`} disabled={busy}
              onClick={() => write({ event: a.event, playbook: a.playbook, scope: a.scope, filter: a.filter, active: !a.active })}
              aria-pressed={a.active} aria-label={a.active ? 'Switch off' : 'Switch on'} title={a.active ? 'Switch off' : 'Switch on'}>
              <span className="knob" />
            </button>
            <button className="icon-btn-plain" style={{ marginLeft: 0 }} disabled={busy}
              onClick={() => remove(a)} title="Delete this rule" aria-label="Delete this rule">
              <Icon name="delete" size={18} />
            </button>
          </div>
        ))}

        {adding ? (
          <div className="rule-builder">
            <div className="rule-sentence">
              <span>When</span>
              <select className="field" value={event} onChange={(e) => setDraft((d) => ({ ...d, event: e.target.value, to: '' }))} aria-label="When">
                {data.events.map((e) => <option key={e.key} value={e.key}>{lower1(e.label)}</option>)}
              </select>
              {filterOptions.length ? (
                <>
                  <span>to</span>
                  <select className="field" value={draft.to} onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))} aria-label="Which stage">
                    <option value="">any stage</option>
                    {filterOptions.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                  </select>
                </>
              ) : null}
              <span>, start</span>
              <select className="field" value={playbook} onChange={(e) => setDraft((d) => ({ ...d, playbook: e.target.value }))} aria-label="Workflow">
                {categories.map((c) => (
                  <optgroup key={c} label={c}>
                    {data.playbooks.filter((p) => p.category === c).map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
                  </optgroup>
                ))}
              </select>
              {isAdmin ? (
                <>
                  <span>for</span>
                  <select className="field" value={draft.scope} onChange={(e) => setDraft((d) => ({ ...d, scope: e.target.value as 'mine' | 'branch' }))} aria-label="Who it applies to">
                    <option value="mine">my contacts</option>
                    <option value="branch">everyone at the branch</option>
                  </select>
                </>
              ) : null}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button className="btn primary" onClick={add} disabled={busy || !event || !playbook}>Add rule</button>
              <button className="btn" onClick={() => setAdding(false)}>Cancel</button>
            </div>
          </div>
        ) : null}

        {suggestions.length && !adding ? (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>
            <span className="quiet" style={{ fontSize: 11.5 }}>Popular:</span>
            {suggestions.map((s) => (
              <button key={ruleKey(s.event, s.playbook, s.filter)} className="slchip dashed" disabled={busy}
                onClick={() => write({ event: s.event, playbook: s.playbook, scope: 'mine', filter: s.filter, active: true },
                  'Done — it starts the next time that happens.')}>
                <Icon name="add" size={13} /> When {when(eventLabel(s.event), s.filter)}, start {playbookName(s.playbook)}
              </button>
            ))}
          </div>
        ) : null}

        <div className="quiet" style={{ fontSize: 11.5, marginTop: 10 }}>
          <Icon name="verified_user" size={13} style={{ verticalAlign: -2 }} /> Texts and emails still only go to people who
          agreed to receive them, never late at night. Contacts added by importing a spreadsheet never start a workflow by themselves.
        </div>
      </div>
    </section>
  )
}
